import type { BookingsFilters, RecommendationQueryParams } from "@/types/api";

/** The caller's linked-bookings scope for metrics, lists, and widgets. */
export interface ViewerBookingScope {
  /** SalesPerson link — always applied when set (cannot be cleared). */
  salesPerson: string | null;
  /** Ops Open By values — applied when the preference is on. */
  openedBy: string[] | null;
  /**
   * Ask the API to apply the auth-resolved linked portfolio (sales OR
   * opened_by) instead of AND-ing the two filter chips.
   */
  linkedScope?: boolean;
}

export const EMPTY_VIEWER_SCOPE: ViewerBookingScope = {
  salesPerson: null,
  openedBy: null,
};

/** Live scope mirrored from `useSyncViewerBookingScope` for deep-link builders. */
let liveScope: ViewerBookingScope = EMPTY_VIEWER_SCOPE;

export function setLiveViewerScope(scope: ViewerBookingScope) {
  liveScope = scope;
}

export function liveViewerScope(): ViewerBookingScope {
  return liveScope;
}

export function resolveViewerBookingScope(input: {
  isSalesPerson: boolean;
  /** Client (consignee portal) — hard-scoped to a consignee server-side. */
  isClient?: boolean;
  salesPersonLink?: string | null;
  openedByValues?: string[] | null;
  preferenceOn: boolean;
  /** Ops Friday override — one operator covers the desk. */
  fridayOverride?: boolean;
}): ViewerBookingScope {
  // SalesPerson scope is enforced server-side as the UNION of their
  // `sales_person` link and any Focus "Open By" assignments. Injecting a
  // client-side `sales_person` / `opened_by` filter here would AND-narrow
  // that union, so we send nothing. A Client is likewise hard-scoped to its
  // linked consignee server-side, so the frontend sends nothing.
  if (input.isSalesPerson || input.isClient) {
    return EMPTY_VIEWER_SCOPE;
  }
  if (!input.preferenceOn) {
    return EMPTY_VIEWER_SCOPE;
  }
  if (input.fridayOverride) {
    return EMPTY_VIEWER_SCOPE;
  }
  const salesPerson = input.salesPersonLink?.trim() || null;
  const openedBy = (input.openedByValues ?? [])
    .map((value) => value.trim())
    .filter(Boolean);
  if (!salesPerson && openedBy.length === 0) {
    return EMPTY_VIEWER_SCOPE;
  }
  return {
    salesPerson,
    openedBy: openedBy.length > 0 ? openedBy : null,
    linkedScope: true,
  };
}

export function viewerScopeKey(scope: ViewerBookingScope): string {
  if (scope.linkedScope) {
    const parts = [
      scope.salesPerson ? `sp:${scope.salesPerson}` : "",
      scope.openedBy?.length ? `ob:${scope.openedBy.join(",")}` : "",
    ].filter(Boolean);
    return `linked:${parts.join("+")}`;
  }
  if (scope.salesPerson) return `sp:${scope.salesPerson}`;
  if (scope.openedBy?.length) return `ob:${scope.openedBy.join(",")}`;
  return "";
}

export function hasViewerScope(scope: ViewerBookingScope): boolean {
  return Boolean(
    scope.linkedScope || scope.salesPerson || scope.openedBy?.length,
  );
}

/**
 * Ops / admin can turn off “show only my bookings” to enlarge a search.
 * SalesPerson and Client accounts stay locked to their linked portfolio.
 */
export function canExpandLinkedSearchScope(input: {
  isSalesPerson: boolean;
  isClient?: boolean;
  salesPersonLink?: string | null;
  openedByValues?: string[] | null;
  preferenceOn: boolean;
  fridayOverride?: boolean;
}): boolean {
  if (input.isSalesPerson || input.isClient) return false;
  return hasViewerScope(resolveViewerBookingScope(input));
}

/** Forced slicer values for dashboard filter bars (not persisted). */
export function viewerScopeForcedLocked(
  scope: ViewerBookingScope,
): Record<string, string> {
  // Linked-scope is a server-side union, not a pair of AND-ed slicers.
  if (scope.linkedScope) return {};
  const locked: Record<string, string> = {};
  if (scope.salesPerson) locked.sales_person = scope.salesPerson;
  if (scope.openedBy?.length) locked.opened_by = scope.openedBy.join(",");
  return locked;
}

function applyLinkedScope<T extends Record<string, unknown>>(
  filters: T,
  scope: ViewerBookingScope,
): T {
  if (!hasViewerScope(scope)) return filters;
  const next = { ...filters };
  if (scope.linkedScope) {
    (next as Record<string, unknown>).linked_scope = true;
    return next;
  }
  if (scope.salesPerson) {
    (next as Record<string, unknown>).sales_person = scope.salesPerson;
    delete (next as Record<string, unknown>).sales_person_not;
  } else if (scope.openedBy?.length) {
    (next as Record<string, unknown>).opened_by = scope.openedBy.join(",");
    delete (next as Record<string, unknown>).opened_by_not;
  }
  return next;
}

export function applyViewerScopeToRecord(
  filters: Record<string, unknown> | undefined,
  scope: ViewerBookingScope,
): Record<string, unknown> {
  return applyLinkedScope({ ...(filters ?? {}) }, scope);
}

export function applyViewerScopeToBookings(
  filters: BookingsFilters,
  scope: ViewerBookingScope,
): BookingsFilters {
  return applyLinkedScope({ ...filters }, scope);
}

/**
 * Scope a recommendations query the same way bookings/metrics are scoped.
 * Dual-linked callers send `linked_scope` (sales OR opened-by) instead of
 * AND-ing `booking_sales_person` + `booking_opened_by`.
 */
export function applyViewerScopeToRecommendations(
  params: RecommendationQueryParams,
  scope: ViewerBookingScope,
): RecommendationQueryParams {
  if (!hasViewerScope(scope)) return { ...params };
  const next = { ...params };
  if (scope.linkedScope) {
    next.linked_scope = true;
    delete next.booking_sales_person;
    delete next.booking_opened_by;
    return next;
  }
  if (scope.salesPerson) {
    next.booking_sales_person = scope.salesPerson;
  } else if (scope.openedBy?.length) {
    next.booking_opened_by = scope.openedBy.join(",");
  }
  return next;
}
